import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRetryDelaySeconds } from "@ease/shared";
import type { WebinarLayoutPreset, WebinarLayoutTransition } from "@ease/shared";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/lib/auth-provider";
import { ApiError, apiFetch } from "@/lib/api-client";

export interface WebinarChatMessage {
  id: string;
  senderId?: string | null;
  senderName: string;
  senderRole: "host" | "co_host" | "moderator" | "attendee" | "system";
  messageType: "text" | "question" | "announcement" | "system" | "reaction";
  content: string;
  parentMessageId?: string | null;
  isModerated?: boolean;
  shadowFiltered?: boolean;
  moderationCategory?: string | null;
  avatarUrl?: string | null;
  createdAt: string;
}

export interface WebinarPoll {
  id: string;
  question: string;
  pollType: "single_choice" | "multiple_choice" | "word_cloud" | "rating" | "open_text";
  options?: Array<{ id: string; text: string }>;
  isAnonymous?: boolean;
  showResultsToAttendees?: boolean;
  status: "draft" | "active" | "closed" | "archived";
  launchedAt?: string | null;
  closedAt?: string | null;
  sortOrder?: number;
}

export interface WebinarPollResults {
  pollId: string;
  pollType: WebinarPoll["pollType"];
  totalResponses: number;
  counts?: Array<{ optionId: string; label: string; responses: number }>;
  average?: number | null;
  textResponses?: string[];
}

export interface WebinarCta {
  id: string;
  sequenceId?: string | null;
  stepIndex?: number;
  stepType?: string | null;
  ctaType: "link" | "form" | "offer" | "poll";
  label: string;
  headline?: string | null;
  description?: string | null;
  url?: string | null;
  productImageUrl?: string | null;
  originalPriceCents?: number | null;
  offerPriceCents?: number | null;
  currency?: string;
  displayAtSeconds?: number | null;
  durationSeconds?: number | null;
  scarcityCount?: number | null;
  scarcityMode?: string | null;
  bonuses?: Array<{ name: string; valueCents: number }>;
  testimonials?: Array<{ name: string; text: string; photoUrl?: string }>;
  clickCount?: number;
  createdAt?: string | null;
  countdownEndsAt?: string | null;
}

export interface WebinarHandout {
  name: string;
  r2Key: string;
  releasedAt: string;
}

export interface WebinarSlideAsset {
  id: string;
  name: string;
  r2Key: string;
  url: string;
  contentType: string;
  sizeBytes: number;
}

export interface WebinarSlideState {
  title: string;
  slides: WebinarSlideAsset[];
  currentIndex: number;
  currentSlide: WebinarSlideAsset | null;
  updatedAt: string;
}

export interface WebinarReactionEvent {
  reactionType: "fire" | "clap" | "heart" | "laugh" | "party" | "thumbs_up";
  userId?: string | null;
  userName?: string | null;
  timestamp: string;
}

export interface WebinarRaisedHand {
  participantId: string;
  displayName: string | null;
  raisedAt: string | null;
  spotlightedAt?: string | null;
}

export interface WebinarSpotlightState {
  participantId: string | null;
  displayName: string | null;
  updatedAt: string | null;
}

export interface WebinarQuestion {
  id: string;
  contactId: string;
  question: string;
  answer?: string | null;
  answeredAt?: string | null;
  upvotes: number;
  isPinned: boolean;
  isHighlighted: boolean;
  aiSuggestedAnswer?: string | null;
  createdAt: string;
}

export interface WebinarPurchaseEvent {
  id: string;
  buyerName: string;
  location?: string | null;
  offerId: string;
  amountCents: number;
  isSimulated: boolean;
  timestamp: string;
}

export interface WebinarGamificationEntry {
  participantId: string;
  displayName: string;
  points: number;
  achievements: string[];
  chatCount: number;
  questionCount: number;
  pollResponseCount: number;
  reactionCount: number;
  upvoteCount: number;
  updatedAt: string;
}

export interface WebinarAchievementEvent {
  participantId: string;
  displayName: string;
  label: string;
  points: number;
  timestamp: string;
}

interface RealtimeEnvelope {
  type?: string;
  payload?: Record<string, unknown>;
}

export interface WebinarLayoutState {
  preset: WebinarLayoutPreset;
  transition: WebinarLayoutTransition;
  customConfig?: Record<string, unknown> | null;
  updatedAt?: string;
}

const RECONNECT_MAX_ATTEMPTS = 10;
const REACTION_TTL_MS = 4_000;

export const __webinarRealtimeInternals = {
  buildRealtimeWebsocketUrl,
  extractBearerToken,
  parseEnvelope,
};

export function useWebinarRealtime(webinarId: string | undefined) {
  const { session, isLoading: isAuthLoading } = useAuth();
  const personId = session?.user.id ?? undefined;
  const displayName = session?.user.name ?? "Attendee";

  const [messages, setMessages] = useState<WebinarChatMessage[]>([]);
  const [polls, setPolls] = useState<WebinarPoll[]>([]);
  const [pollResultsById, setPollResultsById] = useState<Record<string, WebinarPollResults | undefined>>({});
  const [questions, setQuestions] = useState<WebinarQuestion[]>([]);
  const [activeCta, setActiveCta] = useState<WebinarCta | null>(null);
  const [recentPurchases, setRecentPurchases] = useState<WebinarPurchaseEvent[]>([]);
  const [leaderboard, setLeaderboard] = useState<WebinarGamificationEntry[]>([]);
  const [achievementToasts, setAchievementToasts] = useState<WebinarAchievementEvent[]>([]);
  const [handouts, setHandouts] = useState<WebinarHandout[]>([]);
  const [activeSlideState, setActiveSlideState] = useState<WebinarSlideState | null>(null);
  const [attendeeCount, setAttendeeCount] = useState(0);
  const [recentReactions, setRecentReactions] = useState<WebinarReactionEvent[]>([]);
  const [raisedHands, setRaisedHands] = useState<WebinarRaisedHand[]>([]);
  const [spotlight, setSpotlight] = useState<WebinarSpotlightState>({
    participantId: null,
    displayName: null,
    updatedAt: null,
  });
  const [layout, setLayout] = useState<WebinarLayoutState>({
    preset: "speaker_only",
    transition: "crossfade",
    customConfig: null,
  });
  const [isConnected, setIsConnected] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [questionError, setQuestionError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const joinStartedAt = useRef<number | null>(null);
  const ctaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reactionTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const activePoll = useMemo(
    () => polls.find((poll) => poll.status === "active") ?? null,
    [polls],
  );

  const refreshBootstrapData = useCallback(async () => {
    if (!webinarId) {
      return;
    }

    try {
      const [chatResponse, pollResponse, questionResponse, handoutResponse, attendeeResponse, layoutResponse, slideResponse, gamificationResponse, handRaiseResponse] = await Promise.all([
        apiFetch<{ data?: WebinarChatMessage[] }>(`/v1/webinars/${webinarId}/chat`),
        apiFetch<{ data?: WebinarPoll[] }>(`/v1/webinars/${webinarId}/polls`),
        apiFetch<{ data?: WebinarQuestion[] }>(`/v1/webinars/${webinarId}/questions`),
        apiFetch<{ data?: WebinarHandout[] }>(`/v1/webinars/${webinarId}/handouts`),
        apiFetch<{ data?: Array<{ leftAt?: string | null }> }>(`/v1/webinars/${webinarId}/attendance`),
        apiFetch<{ data?: WebinarLayoutState }>(`/v1/webinars/${webinarId}/control-room/layout`),
        apiFetch<{ data?: WebinarSlideState | null }>(`/v1/webinars/${webinarId}/slides/current`),
        apiFetch<{ data?: { leaderboard?: WebinarGamificationEntry[]; participant?: WebinarGamificationEntry | null } }>(`/v1/webinars/${webinarId}/gamification/leaderboard`),
        apiFetch<{ data?: { queue?: WebinarRaisedHand[]; spotlightParticipantId?: string | null; spotlightDisplayName?: string | null; spotlightUpdatedAt?: string | null } }>(`/v1/webinars/${webinarId}/hand-raise`),
      ]);

      setMessages(chatResponse.data ?? []);
      setPolls(pollResponse.data ?? []);
      setQuestions(sortQuestions(questionResponse.data ?? []));
      setHandouts(handoutResponse.data ?? []);
      setAttendeeCount((attendeeResponse.data ?? []).filter((attendee) => attendee.leftAt == null).length);
      if (layoutResponse.data) {
        setLayout(layoutResponse.data);
      }
      if (slideResponse.data) {
        setActiveSlideState(slideResponse.data);
      }
      const bootstrappedLeaderboard = gamificationResponse.data?.leaderboard ?? [];
      const currentParticipant = gamificationResponse.data?.participant;
      setLeaderboard(mergeLeaderboardEntries(bootstrappedLeaderboard, currentParticipant ? [currentParticipant] : []));
      setRaisedHands(handRaiseResponse.data?.queue ?? []);
      setSpotlight({
        participantId: handRaiseResponse.data?.spotlightParticipantId ?? null,
        displayName: handRaiseResponse.data?.spotlightDisplayName ?? null,
        updatedAt: handRaiseResponse.data?.spotlightUpdatedAt ?? null,
      });
    } catch {
      // Bootstrap load is best-effort. The websocket stream will still mount.
    }
  }, [webinarId]);

  const scheduleReconnect = useCallback((reconnect: () => Promise<void>) => {
    if (reconnectAttempts.current >= RECONNECT_MAX_ATTEMPTS) {
      return;
    }

    const delayMs = getRetryDelaySeconds(reconnectAttempts.current + 1, { baseMs: 1_000 }) * 1_000;
    reconnectAttempts.current += 1;
    reconnectTimer.current = setTimeout(() => {
      void reconnect();
    }, delayMs);
  }, []);

  const connect = useCallback(async () => {
    if (!webinarId || isAuthLoading) {
      return;
    }

    const bearerToken = await requestBearerToken();
    if (!bearerToken) {
      setConnectError("Webinar realtime requires an authenticated session.");
      return;
    }

    setConnectError(null);

    try {
      const ws = new WebSocket(
        buildRealtimeWebsocketUrl(resolveRealtimeBaseUrl(), bearerToken, webinarId),
      );
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      });

      ws.addEventListener("message", (event) => {
        const parsed = parseEnvelope(event.data);
        if (!parsed?.type || !parsed.payload) {
          return;
        }

        if (parsed.type === "webinar.chat.message") {
          const nextMessage = pickRecord(parsed.payload, "message");
          if (nextMessage) {
            setMessages((current) => mergeChatMessages(current, [nextMessage as unknown as WebinarChatMessage]));
          }
          return;
        }

        if (parsed.type === "webinar.chat.message_moderated") {
          const messageId = parsed.payload.messageId;
          if (typeof messageId === "string") {
            setMessages((current) => current.map((message) => (
              message.id === messageId
                ? { ...message, isModerated: true, content: "[Message removed by moderator]" }
                : message
            )));
          }
          return;
        }

        if (parsed.type === "webinar.poll.launched" || parsed.type === "webinar.poll.closed") {
          const poll = pickRecord(parsed.payload, "poll");
          if (poll) {
            setPolls((current) => mergePolls(current, [poll as unknown as WebinarPoll]));
          }
          return;
        }

        if (parsed.type === "webinar.poll.results") {
          const poll = pickRecord(parsed.payload, "poll");
          const results = pickRecord(parsed.payload, "results");
          if (poll) {
            setPolls((current) => mergePolls(current, [poll as unknown as WebinarPoll]));
          }
          if (results && typeof results.pollId === "string") {
            const pollId = results.pollId;
            setPollResultsById((current) => ({
              ...current,
              [pollId]: results as unknown as WebinarPollResults,
            }));
          }
          return;
        }

        if (parsed.type === "webinar.cta.triggered") {
          const cta = pickRecord(parsed.payload, "cta");
          if (!cta) {
            return;
          }

          const nextCta = {
            ...(cta as unknown as WebinarCta),
            countdownEndsAt: typeof cta.durationSeconds === "number" && cta.durationSeconds > 0
              ? new Date(Date.now() + cta.durationSeconds * 1_000).toISOString()
              : null,
          } satisfies WebinarCta;
          setActiveCta(nextCta);
          if (typeof nextCta.sequenceId === "string") {
            void apiFetch(`/v1/webinars/${webinarId}/cta-sequences/track`, {
              method: "POST",
              body: JSON.stringify({
                sequenceId: nextCta.sequenceId,
                eventType: "impression",
              }),
            }).catch(() => undefined);
          }
          if (ctaTimer.current) {
            clearTimeout(ctaTimer.current);
          }
          if (typeof nextCta.durationSeconds === "number" && nextCta.durationSeconds > 0) {
            ctaTimer.current = setTimeout(() => {
              setActiveCta((current) => (current?.id === nextCta.id ? null : current));
            }, nextCta.durationSeconds * 1_000);
          }
          return;
        }

        if (parsed.type === "webinar.question.created" || parsed.type === "webinar.question.updated") {
          const question = pickRecord(parsed.payload, "question");
          if (question) {
            setQuestions((current) => mergeQuestions(current, [question as unknown as WebinarQuestion]));
          }
          return;
        }

        if (parsed.type === "webinar.attendee.count_updated") {
          if (typeof parsed.payload.attendeeCount === "number") {
            setAttendeeCount(parsed.payload.attendeeCount);
          }
          return;
        }

        if (parsed.type === "webinar.hand.raised") {
          const participant = pickRecord(parsed.payload, "participant");
          if (participant && typeof participant.participantId === "string") {
            setRaisedHands((current) => mergeRaisedHands(current, [participant as unknown as WebinarRaisedHand]));
          }
          return;
        }

        if (parsed.type === "webinar.hand.lowered") {
          const loweredParticipantId = typeof parsed.payload.participantId === "string"
            ? parsed.payload.participantId
            : null;
          if (loweredParticipantId) {
            setRaisedHands((current) => current.filter((entry) => entry.participantId !== loweredParticipantId));
          }
          return;
        }

        if (parsed.type === "webinar.hand.lowered_all") {
          setRaisedHands([]);
          return;
        }

        if (parsed.type === "webinar.spotlight.changed") {
          const spotlightParticipantId = typeof parsed.payload.spotlightParticipantId === "string"
            ? parsed.payload.spotlightParticipantId
            : null;
          const spotlightDisplayName = typeof parsed.payload.spotlightDisplayName === "string"
            ? parsed.payload.spotlightDisplayName
            : null;
          const spotlightUpdatedAt = typeof parsed.payload.spotlightUpdatedAt === "string"
            ? parsed.payload.spotlightUpdatedAt
            : null;

          setSpotlight({
            participantId: spotlightParticipantId,
            displayName: spotlightDisplayName,
            updatedAt: spotlightUpdatedAt,
          });
          setRaisedHands((current) => current.map((entry) => ({
            ...entry,
            spotlightedAt: entry.participantId === spotlightParticipantId
              ? spotlightUpdatedAt
              : null,
          })));
          return;
        }

        if (parsed.type === "webinar.reaction.created") {
          const reaction = pickRecord(parsed.payload, "reaction");
          if (!reaction) {
            return;
          }

          const nextReaction = reaction as unknown as WebinarReactionEvent;
          setRecentReactions((current) => [...current, nextReaction].slice(-10));
          const timer = setTimeout(() => {
            setRecentReactions((current) => current.filter((entry) => entry.timestamp !== nextReaction.timestamp));
          }, REACTION_TTL_MS);
          reactionTimers.current.push(timer);
          return;
        }

        if (parsed.type === "webinar.purchase") {
          const purchase = pickRecord(parsed.payload, "purchase");
          if (!purchase) {
            return;
          }

          const nextPurchase = purchase as unknown as WebinarPurchaseEvent;
          setRecentPurchases((current) => [nextPurchase, ...current].slice(0, 10));
          setActiveCta((current) => {
            if (!current || current.scarcityMode !== "real") {
              return current;
            }
            return {
              ...current,
              scarcityCount: current.scarcityCount != null ? Math.max(0, current.scarcityCount - 1) : current.scarcityCount,
            };
          });
          return;
        }

        if (parsed.type === "webinar.gamification.update") {
          const nextLeaderboard = Array.isArray(parsed.payload.leaderboard)
            ? parsed.payload.leaderboard.filter((entry): entry is WebinarGamificationEntry => isPlainObject(entry))
            : [];
          setLeaderboard(mergeLeaderboardEntries(nextLeaderboard));
          return;
        }

        if (parsed.type === "webinar.gamification.achievement") {
          const achievement = pickRecord(parsed.payload, "achievement");
          if (!achievement) {
            return;
          }

          const nextAchievement = achievement as unknown as WebinarAchievementEvent;
          setAchievementToasts((current) => [nextAchievement, ...current].slice(0, 3));
          const timer = setTimeout(() => {
            setAchievementToasts((current) => current.filter((entry) => entry.timestamp !== nextAchievement.timestamp));
          }, REACTION_TTL_MS);
          reactionTimers.current.push(timer);
          setLeaderboard((current) => mergeLeaderboardEntries(current, [{
            participantId: nextAchievement.participantId,
            displayName: nextAchievement.displayName,
            points: nextAchievement.points,
            achievements: [nextAchievement.label],
            chatCount: 0,
            questionCount: 0,
            pollResponseCount: 0,
            reactionCount: 0,
            upvoteCount: 0,
            updatedAt: nextAchievement.timestamp,
          }]));
          return;
        }

        if (parsed.type === "webinar.handout.released") {
          const handout = pickRecord(parsed.payload, "handout");
          if (handout) {
            setHandouts((current) => [...current, handout as unknown as WebinarHandout]);
          }
          return;
        }

        if (parsed.type === "webinar.control_room.layout_changed") {
          if (typeof parsed.payload.preset === "string" && typeof parsed.payload.transition === "string") {
            setLayout({
              preset: parsed.payload.preset as WebinarLayoutPreset,
              transition: parsed.payload.transition as WebinarLayoutTransition,
              customConfig: pickRecord(parsed.payload, "customConfig") ?? null,
              updatedAt: typeof parsed.payload.updatedAt === "string" ? parsed.payload.updatedAt : undefined,
            });
          }
          return;
        }

        if (parsed.type === "webinar.slide.changed") {
          const currentSlide = pickRecord(parsed.payload, "currentSlide");
          const slides = Array.isArray(parsed.payload.slides)
            ? parsed.payload.slides.filter((entry): entry is WebinarSlideAsset => isPlainObject(entry))
            : [];
          if (typeof parsed.payload.title === "string" && typeof parsed.payload.currentIndex === "number") {
            setActiveSlideState({
              title: parsed.payload.title,
              slides,
              currentIndex: parsed.payload.currentIndex,
              currentSlide: currentSlide as WebinarSlideAsset | null,
              updatedAt: typeof parsed.payload.updatedAt === "string" ? parsed.payload.updatedAt : new Date().toISOString(),
            });
          }
        }
      });

      ws.addEventListener("close", () => {
        setIsConnected(false);
        wsRef.current = null;
        scheduleReconnect(connect);
      });

      ws.addEventListener("error", () => {
        setConnectError("Webinar realtime unavailable");
      });
    } catch {
      setConnectError("Webinar realtime unavailable");
      setIsConnected(false);
      scheduleReconnect(connect);
    }
  }, [isAuthLoading, scheduleReconnect, webinarId]);

  useEffect(() => {
    void refreshBootstrapData();
  }, [refreshBootstrapData]);

  useEffect(() => {
    if (!webinarId || !personId) {
      return;
    }

    joinStartedAt.current = Date.now();
    void apiFetch(`/v1/webinars/${webinarId}/attendance/join`, {
      method: "POST",
      body: JSON.stringify({ personId }),
    }).catch(() => undefined);

    return () => {
      const startedAt = joinStartedAt.current;
      const watchDurationSeconds = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : 0;
      void apiFetch(`/v1/webinars/${webinarId}/attendance/leave`, {
        method: "POST",
        body: JSON.stringify({ personId, watchDurationSeconds }),
      }).catch(() => undefined);
    };
  }, [personId, webinarId]);

  useEffect(() => {
    void connect();

    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      if (ctaTimer.current) {
        clearTimeout(ctaTimer.current);
      }
      for (const timer of reactionTimers.current) {
        clearTimeout(timer);
      }
      reactionTimers.current = [];
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  const sendChatMessage = useCallback(async (content: string) => {
    if (!webinarId) {
      return null;
    }

    try {
      setChatError(null);
      const response = await apiFetch<{ data: WebinarChatMessage }>(`/v1/webinars/${webinarId}/chat`, {
        method: "POST",
        body: JSON.stringify({
          content,
          senderName: displayName,
        }),
      });

      if (response.data) {
        setMessages((current) => mergeChatMessages(current, [response.data]));
      }
      return response.data ?? null;
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Unable to send message";
      setChatError(message);
      return null;
    }
  }, [displayName, webinarId]);

  const sendReaction = useCallback(async (reactionType: WebinarReactionEvent["reactionType"]) => {
    if (!webinarId) {
      return;
    }

    await apiFetch(`/v1/webinars/${webinarId}/reactions`, {
      method: "POST",
      body: JSON.stringify({ reactionType }),
    });
  }, [webinarId]);

  const submitPollResponse = useCallback(async (pollId: string, payload: {
    selectedOptionIds?: string[];
    ratingValue?: number;
    textResponse?: string;
  }) => {
    if (!webinarId) {
      return;
    }

    await apiFetch(`/v1/webinars/${webinarId}/polls/${pollId}/respond`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }, [webinarId]);

  const submitQuestion = useCallback(async (question: string) => {
    if (!webinarId) {
      return false;
    }

    try {
      setQuestionError(null);
      const response = await apiFetch<{ data: WebinarQuestion }>(`/v1/webinars/${webinarId}/questions`, {
        method: "POST",
        body: JSON.stringify({ question }),
      });

      if (response.data) {
        setQuestions((current) => mergeQuestions(current, [response.data]));
      }
      return true;
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Unable to submit question";
      setQuestionError(message);
      return false;
    }
  }, [webinarId]);

  const upvoteQuestion = useCallback(async (questionId: string) => {
    if (!webinarId) {
      return;
    }

    try {
      setQuestionError(null);
      const response = await apiFetch<{ data?: WebinarQuestion }>(`/v1/webinars/${webinarId}/questions/${questionId}/upvote`, {
        method: "POST",
      });
      const nextQuestion = response.data;
      if (nextQuestion) {
        setQuestions((current) => mergeQuestions(current, [nextQuestion]));
      }
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Unable to upvote question";
      setQuestionError(message);
    }
  }, [webinarId]);

  const trackCtaClick = useCallback(async (cta: WebinarCta) => {
    if (!webinarId || !cta.sequenceId) {
      return;
    }

    await apiFetch(`/v1/webinars/${webinarId}/cta-sequences/track`, {
      method: "POST",
      body: JSON.stringify({
        sequenceId: cta.sequenceId,
        eventType: "click",
      }),
    });
  }, [webinarId]);

  const raiseHand = useCallback(async () => {
    if (!webinarId) {
      return false;
    }

    await apiFetch(`/v1/webinars/${webinarId}/hand-raise`, {
      method: "POST",
    });
    return true;
  }, [webinarId]);

  const lowerHand = useCallback(async () => {
    if (!webinarId) {
      return false;
    }

    await apiFetch(`/v1/webinars/${webinarId}/hand-raise`, {
      method: "DELETE",
    });
    return true;
  }, [webinarId]);

  return {
    currentParticipantId: personId ?? null,
    messages,
    polls,
    activePoll,
    pollResultsById,
    questions,
    activeCta,
    handouts,
    activeSlideState,
    attendeeCount,
    raisedHands,
    spotlight,
    recentReactions,
    recentPurchases,
    leaderboard,
    achievementToasts,
    layout,
    isConnected,
    connectError,
    chatError,
    questionError,
    sendChatMessage,
    sendReaction,
    submitPollResponse,
    submitQuestion,
    upvoteQuestion,
    trackCtaClick,
    raiseHand,
    lowerHand,
    dismissCta: () => setActiveCta(null),
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function requestBearerToken(): Promise<string | null> {
  const browserProofToken = typeof window !== "undefined"
    ? (window as typeof window & { __WEBINAR_BROWSER_PROOF_TOKEN__?: string }).__WEBINAR_BROWSER_PROOF_TOKEN__
    : undefined;
  if (browserProofToken?.trim()) {
    return browserProofToken;
  }

  const tokenMethod = (authClient as unknown as { token?: () => Promise<unknown> }).token;
  if (typeof tokenMethod !== "function") {
    return null;
  }

  try {
    const result = await tokenMethod.call(authClient);
    return extractBearerToken(result);
  } catch {
    return null;
  }
}

function resolveRealtimeBaseUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env.VITE_REALTIME_URL?.trim() || window.location.origin;
}

function buildRealtimeWebsocketUrl(
  baseUrl: string,
  token: string,
  webinarId: string,
): string {
  const url = new URL(baseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  url.searchParams.set("token", token);
  url.searchParams.set("channels", `webinar.${webinarId}.*`);
  return url.toString();
}

function extractBearerToken(result: unknown): string | null {
  const candidates = [
    pickValue(result, ["data", "token"]),
    pickValue(result, ["data", "data", "token"]),
    pickValue(result, ["token"]),
    pickValue(result, ["data", "accessToken"]),
    pickValue(result, ["data", "data", "accessToken"]),
    pickValue(result, ["accessToken"]),
  ];

  return candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0) ?? null;
}

function parseEnvelope(raw: unknown): RealtimeEnvelope | null {
  try {
    return JSON.parse(typeof raw === "string" ? raw : String(raw)) as RealtimeEnvelope;
  } catch {
    return null;
  }
}

function pickValue(input: unknown, path: string[]): unknown {
  let current: unknown = input;
  for (const segment of path) {
    if (!current || typeof current !== "object" || !(segment in current)) {
      return null;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function pickRecord(input: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const value = input[key];
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function mergeRaisedHands(current: WebinarRaisedHand[], incoming: WebinarRaisedHand[]) {
  const next = new Map<string, WebinarRaisedHand>();
  for (const entry of current) {
    next.set(entry.participantId, entry);
  }
  for (const entry of incoming) {
    next.set(entry.participantId, entry);
  }
  return Array.from(next.values()).sort((left, right) => String(left.raisedAt ?? "").localeCompare(String(right.raisedAt ?? "")));
}

function mergeChatMessages(current: WebinarChatMessage[], incoming: WebinarChatMessage[]) {
  const byId = new Map<string, WebinarChatMessage>();
  for (const message of current) {
    byId.set(message.id, message);
  }
  for (const message of incoming) {
    byId.set(message.id, message);
  }

  return [...byId.values()].sort((left, right) => (
    new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  ));
}

function mergeQuestions(current: WebinarQuestion[], incoming: WebinarQuestion[]) {
  const byId = new Map<string, WebinarQuestion>();
  for (const question of current) {
    byId.set(question.id, question);
  }
  for (const question of incoming) {
    byId.set(question.id, question);
  }

  return sortQuestions([...byId.values()]);
}

function mergeLeaderboardEntries(
  current: WebinarGamificationEntry[],
  incoming: WebinarGamificationEntry[] = [],
) {
  const byId = new Map<string, WebinarGamificationEntry>();
  for (const entry of current) {
    byId.set(entry.participantId, entry);
  }
  for (const entry of incoming) {
    const existing = byId.get(entry.participantId);
    byId.set(entry.participantId, {
      ...existing,
      ...entry,
      achievements: entry.achievements?.length
        ? entry.achievements
        : existing?.achievements ?? [],
    });
  }

  return [...byId.values()].sort((left, right) => {
    if (left.points !== right.points) {
      return right.points - left.points;
    }
    return left.updatedAt < right.updatedAt ? 1 : -1;
  });
}

function sortQuestions(questions: WebinarQuestion[]) {
  return [...questions].sort((left, right) => {
    if (left.isPinned !== right.isPinned) {
      return left.isPinned ? -1 : 1;
    }
    if (left.upvotes !== right.upvotes) {
      return right.upvotes - left.upvotes;
    }
    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });
}

function mergePolls(current: WebinarPoll[], incoming: WebinarPoll[]) {
  const byId = new Map<string, WebinarPoll>();
  for (const poll of current) {
    byId.set(poll.id, poll);
  }
  for (const poll of incoming) {
    byId.set(poll.id, poll);
  }

  return [...byId.values()].sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0));
}
